import { Config, fetchJSON, getLanguageFromLocale } from '@ratehub/base-ui';
import { LOCALES } from '@ratehub/documents/common/stable';

import INSURANCE_TYPES from '../definitions/InsuranceTypes';


async function fetchProviderDetails(insuranceType = undefined, locale = LOCALES.ENGLISH) {
    const verifiedInsuranceType = insuranceType === INSURANCE_TYPES.LEAD_FORM
        ? 'other' // ULF is set up as "other" in the CMS
        : insuranceType;

    // Provider service just accepts "en" or "fr"
    const language = getLanguageFromLocale(locale);

    const url = insuranceType
        // Example: https://api.ratehub.dev/provider-service/providers/businessUnits/auto-insurance?language=en
        ? `${Config.PROVIDER_SERVICE_URL}/providers/businessUnits/${verifiedInsuranceType}-insurance?language=${language}`
        : `${Config.PROVIDER_SERVICE_URL}/providers`;

    const response = await fetchJSON(
        {
            url,
            options: {
                method: 'get',
                credentials: 'same-origin',
            },
        },
        'fetchProviderDetails',
    );

    return {
        insurers: mapInsurers(response.data, verifiedInsuranceType),
        brokers: mapBrokers(response.data, verifiedInsuranceType),
    };
}

function mapBrokers(data, insuranceType) {
    function isBrokerage(provider) {
        return provider?.carousel
            || provider?.type === 'brokerage'; // For brokers without carousel
    }

    return data
        .filter(provider => {
            // Bail if we're not an insurance provider
            if (provider.insurance == null) {
                return false;
            }
            
            if (insuranceType) {
                return isBrokerage(provider.insurance[insuranceType]);
            } else {
                // Check all insurance types if none provided
                return Object.values(provider.insurance).some(isBrokerage);
            }
        })
        .reduce((brokersList, insurer) => {
            if (insuranceType) {
                addBrokersList(brokersList, insurer.insurance[insuranceType].carousel, insurer);
            } else {
                Object.values(insurer.insurance).forEach(vertical => {
                    addBrokersList(brokersList, vertical.carousel, insurer);
                });
            }

            return brokersList;
        }, [])
        .map(broker => ({
            id: broker.ids.primary,
            name: broker.name,
            logoPath: broker.logo,
            description: broker.descriptions.full,

            // All insurance verticals will have the same brokerage info
            phoneNumber: Object.values(broker.insurance)[0].contact.phone,
            hours: Object.values(broker.insurance)[0].office_hours,

            isPrimaryFor: getPrimaryVerticals(broker),
            provinces: broker.insurance?.[insuranceType]?.provinces ?? [],
        }));
}

function mapInsurers(data, insuranceType) {
    function isInsurer(provider) {
        return provider?.type === 'insurer';
    }

    return data
        .filter(provider => {
            // Bail if we're not an insurance provider
            if (provider.insurance == null) {
                return false;
            }

            if (insuranceType) {
                return isInsurer(provider.insurance?.[insuranceType]);
            } else {
                // Check all insurance types if none provided
                return Object.values(provider.insurance).some(isInsurer);
            }
        })
        .map(insurer => {
            const insurerObject = {
                id: insurer.ids.primary,
                name: insurer.name,
                logoPath: insurer.logo,
                description: insurer.descriptions.full,
            };

            if (insuranceType) {
                insurerObject.discounts = insurer.insurance[insuranceType].discounts;
                insurerObject.providerHighlights = insurer.insurance[insuranceType].provider_highlights;
                insurerObject.featuredHighlights = insurer.insurance[insuranceType].featured_highlights;
            }
            
            return insurerObject;
        });
}

function addBrokersList(brokersList, carousel, insurer) {
    if (carousel) {
        addCarouselBrokerages(brokersList, carousel);
    } else {
        addDirectBrokerage(brokersList, insurer);
    }
}

function addCarouselBrokerages(brokersList, carousel) {
    const carouselBrokerages = carousel
        .flatMap(region => region.brokerages)
        .map(brokerageInfo => brokerageInfo.brokerage);

    carouselBrokerages.forEach(brokerage => {
        if (brokersList.every(broker => broker.ids.primary !== brokerage.ids.primary)) {
            brokersList.push(brokerage);
        }
    });
}

function addDirectBrokerage(brokersList, insurer) {
    // Avoid adding duplicates
    if (brokersList.every(broker => broker.ids.primary !== insurer.ids.primary)) {
        // Provider determined by BE
        // - used for life and moto
        // - can be removed once all verticals are using quote comparison
        brokersList.push(insurer);
    }
}

function getPrimaryVerticals(broker) {
    const verticalsArray = Object.entries(broker.insurance)
        .filter(([ /* vertical */, details ]) => details.primary_provider)
        .map(([ vertical ]) => vertical);

    return verticalsArray.length ? verticalsArray : undefined;
}

export default fetchProviderDetails;
